import type { FunctionComponent } from 'react';
import { useEffect } from 'react';

import { Logger } from '../logger/Logger';

import type { IncomingMessage } from './types';

export interface MessageReceiverProps {
  /** Function to handle incoming messages, receives the message as an argument */
  handler: (message: IncomingMessage) => void;
  /** Callback to be called when the component starts listening */
  onListening?: () => void;
}

/**
 * A component that listens for messages from the other windows and calls a handler function when a message is received
 * */
export const MessageReceiver: FunctionComponent<MessageReceiverProps> = ({ handler, onListening }) => {
  useEffect(() => {
    const handleMessages = (event: MessageEvent): void => {
      const message = event.data;
      if (!message) return;
      // Lobby messages
      try {
        handler(message);
      } catch (error) {
        Logger.logFailure('messageReceiver', {
          place: 'messageParsing',
          additional: message?.name,
        });
      }
    };

    window.addEventListener('message', handleMessages);
    if (onListening) onListening();
    return (): void => window.removeEventListener('message', handleMessages);
  }, [handler, onListening]);

  return null;
};
